import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  WorkflowInstance,
  WorkflowStep,
  WorkflowStepState,
  WorkflowStepResult,
  WorkflowState,
  WorkflowDecision,
  WorkflowProgress,
  AddWorkflowStepRequest
} from '../../models/file-vault.models';

export interface ChangeWorkflowStepEvent {
  action: 'approved' | 'rejected' | 'delegated' | 'skipped' | 'claimed';
  step: WorkflowStep;
  result?: WorkflowStepResult;
  workflowComplete: boolean;
  workflowState?: WorkflowState;
}

@Component({
  selector: 'fv-change-workflow',
  standalone: true,
  template: `
    <!-- Loading -->
    <div class="loading" *ngIf="isLoading">Loading workflow...</div>

    <!-- No workflow -->
    <div class="no-workflow" *ngIf="!isLoading && !workflow">
      <span class="no-workflow-icon">📋</span>
      <p>No active workflow for this change.</p>
    </div>

    <!-- Workflow content -->
    <div class="change-workflow" *ngIf="!isLoading && workflow">
      <!-- Header -->
      <div class="header">
        <div class="header-left">
          <h3 class="title">{{ workflow.templateName || 'Workflow' }}</h3>
          <span class="state-badge" [ngClass]="getWorkflowStateClass(workflow.state)">
            {{ getWorkflowStateLabel(workflow.state) }}
          </span>
        </div>
        <div class="header-right">
          <span class="progress-text">
            {{ progress?.completedSteps || 0 }} of {{ progress?.totalSteps || workflow.steps.length }} steps complete
          </span>
          <button kendoButton
                  [look]="'flat'"
                  (click)="openAddStepDialog()"
                  *ngIf="canModifySteps"
                  [disabled]="isProcessing">
            + Add Step
          </button>
          <button kendoButton [look]="'flat'" (click)="reload()">Refresh</button>
        </div>
      </div>

      <!-- Progress bar -->
      <div class="progress-bar-container" *ngIf="progress">
        <div class="progress-bar" [style.width.%]="progress.percentComplete"></div>
      </div>

      <!-- Steps timeline -->
      <div class="steps-timeline">
        <div class="step-row"
             *ngFor="let step of workflow.steps; let i = index"
             [ngClass]="getStepRowClass(step)">

          <!-- Step indicator -->
          <div class="step-indicator">
            <div class="step-dot" [ngClass]="getStepDotClass(step)">
              <span class="step-icon">{{ getStepIcon(step) }}</span>
            </div>
            <div class="step-connector" *ngIf="i < workflow.steps.length - 1"></div>
          </div>

          <!-- Step content -->
          <div class="step-content">
            <div class="step-header">
              <span class="step-name">{{ step.stepName }}</span>
              <span class="step-state-badge" [ngClass]="getStepStateBadgeClass(step.state)">
                {{ getStepStateLabel(step.state) }}
              </span>
              <span class="current-tag" *ngIf="isCurrentStep(step)">CURRENT</span>
              <button kendoButton
                      [look]="'flat'"
                      class="remove-step-btn"
                      (click)="openRemoveStepDialog(step)"
                      *ngIf="canRemoveStep(step)"
                      [disabled]="isProcessing"
                      title="Remove this step">
                ✕
              </button>
            </div>

            <!-- Step description -->
            <p class="step-description" *ngIf="step.description">{{ step.description }}</p>

            <!-- Step metadata -->
            <div class="step-meta">
              <span *ngIf="step.assignedTo || step.assignedToRole" class="meta-item">
                <span class="meta-label">Assigned:</span>
                {{ step.assignedTo || step.assignedToRole }}
              </span>
              <span *ngIf="step.dueDate" class="meta-item" [ngClass]="{ 'overdue': isStepOverdue(step) }">
                <span class="meta-label">Due:</span>
                {{ step.dueDate | date:'shortDate' }}
                <span class="overdue-tag" *ngIf="isStepOverdue(step)">OVERDUE</span>
              </span>
              <span *ngIf="step.completedAt" class="meta-item">
                <span class="meta-label">Completed:</span>
                {{ step.completedAt | date:'short' }} by {{ step.completedBy }}
              </span>
              <span *ngIf="step.decision !== undefined && step.decision !== null" class="meta-item">
                <span class="meta-label">Decision:</span>
                <span class="decision-value" [ngClass]="getDecisionClass(step.decision)">
                  {{ getDecisionLabel(step.decision) }}
                </span>
              </span>
            </div>

            <!-- Completed step comments -->
            <div class="step-comments" *ngIf="step.comments && !isCurrentStep(step)">
              <span class="comments-label">Comments:</span>
              <p class="comments-text">"{{ step.comments }}"</p>
            </div>

            <!-- Current step actions (the sign-off form) -->
            <div class="step-actions" *ngIf="isCurrentStep(step) && canAct(step)">

              <!-- Claim button if unclaimed -->
              <div class="claim-section" *ngIf="!step.assignedTo && currentLoginId">
                <p class="claim-hint">This step is unassigned. Claim it to take action.</p>
                <button kendoButton
                        [themeColor]="'primary'"
                        (click)="doClaim(step)"
                        [disabled]="isProcessing">
                  {{ isProcessing ? 'Claiming...' : 'Claim Step' }}
                </button>
              </div>

              <!-- Action form if claimed by current user -->
              <div class="action-form" *ngIf="step.assignedTo && isAssignedToCurrentUser(step)">
                <div class="comments-input">
                  <label>Comments {{ step.requiresComments ? '(required)' : '(optional)' }}</label>
                  <textarea kendoTextArea
                            [(ngModel)]="comments"
                            [rows]="3"
                            placeholder="Enter your comments...">
                  </textarea>
                </div>

                <div class="action-buttons">
                  <button kendoButton
                          [themeColor]="'primary'"
                          (click)="openConfirmDialog('approve', step)"
                          [disabled]="isProcessing || (step.requiresComments && !comments?.trim())"
                          *ngIf="canApprove(step)">
                    Approve
                  </button>
                  <button kendoButton
                          [themeColor]="'error'"
                          (click)="openConfirmDialog('reject', step)"
                          [disabled]="isProcessing"
                          *ngIf="canReject(step)">
                    Reject
                  </button>
                  <button kendoButton
                          (click)="openDelegateDialog(step)"
                          [disabled]="isProcessing">
                    Delegate
                  </button>
                  <button kendoButton
                          [look]="'flat'"
                          (click)="openConfirmDialog('skip', step)"
                          [disabled]="isProcessing"
                          *ngIf="step.canSkip">
                    Skip
                  </button>
                </div>
              </div>

              <!-- Not assigned to current user -->
              <div class="not-assigned-hint"
                   *ngIf="step.assignedTo && !isAssignedToCurrentUser(step)">
                <p>This step is assigned to <strong>{{ step.assignedTo }}</strong>.</p>
              </div>
            </div>

            <!-- Current step but no loginId -->
            <div class="login-hint" *ngIf="isCurrentStep(step) && !currentLoginId && workflow.state === WorkflowState.InProgress">
              <p>Provide a <code>currentLoginId</code> to interact with this step.</p>
            </div>

          </div>
        </div>
      </div>

      <!-- Workflow completion summary -->
      <div class="completion-summary" *ngIf="workflow.state === WorkflowState.Completed">
        <span class="completion-icon">✅</span>
        <div class="completion-text">
          <strong>Workflow Complete</strong>
          <span *ngIf="workflow.completedAt">Completed {{ workflow.completedAt | date:'medium' }}</span>
        </div>
      </div>

      <div class="completion-summary cancelled" *ngIf="workflow.state === WorkflowState.Cancelled">
        <span class="completion-icon">🚫</span>
        <div class="completion-text">
          <strong>Workflow Cancelled</strong>
        </div>
      </div>

      <div class="completion-summary failed" *ngIf="workflow.state === WorkflowState.Failed">
        <span class="completion-icon">❌</span>
        <div class="completion-text">
          <strong>Workflow Failed</strong>
        </div>
      </div>

      <!-- Error display -->
      <div class="error-message" *ngIf="errorMessage">{{ errorMessage }}</div>
    </div>

    <!-- Approve Confirmation -->
    <kendo-dialog *ngIf="showConfirmDialog && pendingAction === 'approve'"
                  title="Confirm Approval"
                  (close)="closeDialogs()">
      <p>Are you sure you want to <strong>approve</strong> the "{{ pendingStep?.stepName }}" step?</p>
      <div class="dialog-comments" *ngIf="comments?.trim()">
        <strong>Your comments:</strong>
        <p>{{ comments }}</p>
      </div>
      <kendo-dialog-actions>
        <button kendoButton (click)="closeDialogs()">Cancel</button>
        <button kendoButton [themeColor]="'primary'" (click)="doApprove()" [disabled]="isProcessing">
          {{ isProcessing ? 'Processing...' : 'Approve' }}
        </button>
      </kendo-dialog-actions>
    </kendo-dialog>

    <!-- Reject Dialog -->
    <kendo-dialog *ngIf="showConfirmDialog && pendingAction === 'reject'"
                  title="Reject Step"
                  (close)="closeDialogs()">
      <p>Please provide a reason for rejecting the "{{ pendingStep?.stepName }}" step:</p>
      <textarea kendoTextArea
                [(ngModel)]="rejectReason"
                [rows]="3"
                placeholder="Reason for rejection (required)"
                class="dialog-textarea">
      </textarea>
      <kendo-dialog-actions>
        <button kendoButton (click)="closeDialogs()">Cancel</button>
        <button kendoButton [themeColor]="'error'" (click)="doReject()" [disabled]="isProcessing || !rejectReason?.trim()">
          {{ isProcessing ? 'Processing...' : 'Reject' }}
        </button>
      </kendo-dialog-actions>
    </kendo-dialog>

    <!-- Skip Dialog -->
    <kendo-dialog *ngIf="showConfirmDialog && pendingAction === 'skip'"
                  title="Skip Step"
                  (close)="closeDialogs()">
      <p>Are you sure you want to skip the "{{ pendingStep?.stepName }}" step?</p>
      <div class="dialog-field">
        <label>Reason (optional):</label>
        <textarea kendoTextArea
                  [(ngModel)]="skipReason"
                  [rows]="2"
                  placeholder="Reason for skipping">
        </textarea>
      </div>
      <kendo-dialog-actions>
        <button kendoButton (click)="closeDialogs()">Cancel</button>
        <button kendoButton (click)="doSkip()" [disabled]="isProcessing">
          {{ isProcessing ? 'Processing...' : 'Skip' }}
        </button>
      </kendo-dialog-actions>
    </kendo-dialog>

    <!-- Delegate Dialog -->
    <kendo-dialog *ngIf="showDelegateDialogVisible"
                  title="Delegate Step"
                  (close)="closeDialogs()">
      <p>Delegate the "{{ pendingStep?.stepName }}" step to another user:</p>
      <div class="dialog-field">
        <label>Delegate to: <span class="required">*</span></label>
        <input kendoTextBox [(ngModel)]="delegateTo" placeholder="Username or LoginId" />
      </div>
      <div class="dialog-field">
        <label>Reason (optional):</label>
        <textarea kendoTextArea
                  [(ngModel)]="delegateReason"
                  [rows]="2"
                  placeholder="Reason for delegation">
        </textarea>
      </div>
      <kendo-dialog-actions>
        <button kendoButton (click)="closeDialogs()">Cancel</button>
        <button kendoButton [themeColor]="'primary'" (click)="doDelegate()" [disabled]="isProcessing || !delegateTo?.trim()">
          {{ isProcessing ? 'Processing...' : 'Delegate' }}
        </button>
      </kendo-dialog-actions>
    </kendo-dialog>

    <!-- Add Step Dialog -->
    <kendo-dialog *ngIf="showAddStepDialog"
                  title="Add Workflow Step"
                  [width]="480"
                  (close)="closeDialogs()">
      <p>Add a new sign-off step to this workflow. It will be inserted after the current step.</p>
      <div class="dialog-field">
        <label>Step Name <span class="required">*</span></label>
        <input kendoTextBox [(ngModel)]="newStep.stepName" placeholder="e.g. Quality Review" />
      </div>
      <div class="dialog-field">
        <label>Description (optional)</label>
        <textarea kendoTextArea
                  [(ngModel)]="newStep.description"
                  [rows]="2"
                  placeholder="Describe what this step involves">
        </textarea>
      </div>
      <div class="dialog-field">
        <label>Assigned Role (optional)</label>
        <input kendoTextBox [(ngModel)]="newStep.assignedToRole" placeholder="e.g. QualityEngineer" />
      </div>
      <div class="dialog-field">
        <label>Assigned User (optional)</label>
        <input kendoTextBox [(ngModel)]="newStep.assignedToUserName" placeholder="Username or LoginId" />
      </div>
      <div class="dialog-field">
        <label>Due Days (optional)</label>
        <input kendoTextBox [(ngModel)]="newStep.dueDays" type="number" placeholder="Number of days" />
      </div>
      <div class="dialog-field toggle-row">
        <label class="toggle">
          <input type="checkbox" [(ngModel)]="newStep.canSkip" />
          Can be skipped
        </label>
        <label class="toggle">
          <input type="checkbox" [(ngModel)]="newStep.requiresComments" />
          Requires comments
        </label>
      </div>
      <kendo-dialog-actions>
        <button kendoButton (click)="closeDialogs()">Cancel</button>
        <button kendoButton [themeColor]="'primary'" (click)="doAddStep()" [disabled]="isProcessing || !newStep.stepName?.trim()">
          {{ isProcessing ? 'Adding...' : 'Add Step' }}
        </button>
      </kendo-dialog-actions>
    </kendo-dialog>

    <!-- Remove Step Confirmation -->
    <kendo-dialog *ngIf="showRemoveStepDialog"
                  title="Remove Workflow Step"
                  (close)="closeDialogs()">
      <p>Are you sure you want to remove the "<strong>{{ removeStepTarget?.stepName }}</strong>" step from this workflow?</p>
      <p class="remove-warning">This action cannot be undone.</p>
      <kendo-dialog-actions>
        <button kendoButton (click)="closeDialogs()">Cancel</button>
        <button kendoButton [themeColor]="'error'" (click)="doRemoveStep()" [disabled]="isProcessing">
          {{ isProcessing ? 'Removing...' : 'Remove Step' }}
        </button>
      </kendo-dialog-actions>
    </kendo-dialog>
  `,
  styles: [`
    .change-workflow {
      padding: 16px;
    }

    .loading {
      padding: 32px;
      text-align: center;
      color: var(--fv-text-secondary);
    }

    .no-workflow {
      padding: 32px;
      text-align: center;
      color: var(--fv-text-muted);
    }

    .no-workflow-icon {
      font-size: 32px;
      display: block;
      margin-bottom: 8px;
    }

    .no-workflow p {
      margin: 0;
      font-size: 14px;
    }

    /* Header */
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      flex-wrap: wrap;
      gap: 8px;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .title {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: var(--fv-text);
    }

    .state-badge {
      display: inline-block;
      padding: 3px 10px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .state-badge.pending { background: var(--fv-status-draft-bg); color: var(--fv-status-draft-fg); }
    .state-badge.in-progress { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .state-badge.completed { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .state-badge.cancelled { background: var(--fv-surface-alt); color: var(--fv-text-muted); }
    .state-badge.failed { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }

    .progress-text {
      font-size: 12px;
      color: var(--fv-text-muted);
    }

    /* Progress bar */
    .progress-bar-container {
      height: 4px;
      background: var(--fv-border);
      border-radius: 2px;
      margin-bottom: 20px;
      overflow: hidden;
    }

    .progress-bar {
      height: 100%;
      background: var(--fv-text-accent);
      border-radius: 2px;
      transition: width 0.3s ease;
    }

    /* Steps timeline */
    .steps-timeline {
      display: flex;
      flex-direction: column;
    }

    .step-row {
      display: flex;
      gap: 16px;
      min-height: 80px;
    }

    .step-row.current {
      background: var(--fv-status-info-bg);
      margin: 0 -16px;
      padding: 12px 16px;
      border-radius: 8px;
      border: 1px solid var(--fv-status-info-fg);
    }

    /* Step indicator (dot + connector) */
    .step-indicator {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 32px;
      flex-shrink: 0;
    }

    .step-dot {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      flex-shrink: 0;
      border: 2px solid var(--fv-border);
      background: var(--fv-surface);
      color: var(--fv-text-muted);
    }

    .step-dot.approved { border-color: var(--fv-status-success-fg); background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .step-dot.rejected { border-color: var(--fv-status-error-fg); background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
    .step-dot.skipped { border-color: var(--fv-text-muted); background: var(--fv-surface-alt); color: var(--fv-text-muted); }
    .step-dot.delegated { border-color: var(--fv-status-purple-fg); background: var(--fv-status-purple-bg); color: var(--fv-status-purple-fg); }
    .step-dot.in-progress { border-color: var(--fv-status-info-fg); background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .step-dot.pending { border-color: var(--fv-border); background: var(--fv-surface-alt); color: var(--fv-text-muted); }

    .step-connector {
      width: 2px;
      flex: 1;
      background: var(--fv-border);
      margin: 4px 0;
      min-height: 20px;
    }

    /* Step content */
    .step-content {
      flex: 1;
      padding-bottom: 20px;
      min-width: 0;
    }

    .step-header {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .step-name {
      font-size: 14px;
      font-weight: 600;
      color: var(--fv-text);
    }

    .step-state-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .step-state-badge.approved { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .step-state-badge.rejected { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }
    .step-state-badge.skipped { background: var(--fv-status-draft-bg); color: var(--fv-text-muted); }
    .step-state-badge.delegated { background: var(--fv-status-purple-bg); color: var(--fv-status-purple-fg); }
    .step-state-badge.in-progress { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .step-state-badge.pending { background: var(--fv-status-draft-bg); color: var(--fv-text-muted); }

    .current-tag {
      display: inline-block;
      padding: 2px 6px;
      border-radius: 3px;
      font-size: 9px;
      font-weight: 700;
      background: var(--fv-status-info-fg);
      color: var(--fv-surface);
      letter-spacing: 0.5px;
    }

    .step-description {
      margin: 4px 0 0 0;
      font-size: 13px;
      color: var(--fv-text-secondary);
    }

    .step-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      margin-top: 6px;
      font-size: 12px;
      color: var(--fv-text-muted);
    }

    .meta-label {
      color: var(--fv-text-muted);
      margin-right: 4px;
    }

    .meta-item.overdue {
      color: var(--fv-status-error-fg);
    }

    .overdue-tag {
      display: inline-block;
      margin-left: 4px;
      padding: 1px 4px;
      background: var(--fv-status-error-fg);
      color: white;
      border-radius: 2px;
      font-size: 9px;
      font-weight: 600;
    }

    .decision-value.approve { color: var(--fv-status-success-fg); font-weight: 500; }
    .decision-value.reject { color: var(--fv-status-error-fg); font-weight: 500; }
    .decision-value.delegate { color: var(--fv-status-purple-fg); font-weight: 500; }

    .step-comments {
      margin-top: 8px;
      font-size: 12px;
    }

    .comments-label {
      color: var(--fv-text-muted);
      font-size: 11px;
    }

    .comments-text {
      margin: 2px 0 0 0;
      color: var(--fv-text);
      font-style: italic;
    }

    /* Current step action form */
    .step-actions {
      margin-top: 16px;
      padding: 16px;
      background: var(--fv-surface);
      border: 1px solid var(--fv-border);
      border-radius: 8px;
    }

    .claim-section {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .claim-hint {
      margin: 0;
      font-size: 13px;
      color: var(--fv-text-secondary);
    }

    .comments-input {
      margin-bottom: 12px;
    }

    .comments-input label {
      display: block;
      margin-bottom: 4px;
      font-size: 13px;
      font-weight: 500;
      color: var(--fv-text);
    }

    .comments-input textarea {
      width: 100%;
    }

    .action-buttons {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }

    .not-assigned-hint {
      font-size: 13px;
      color: var(--fv-text-secondary);
    }

    .not-assigned-hint p {
      margin: 0;
    }

    .login-hint {
      margin-top: 8px;
      font-size: 12px;
      color: var(--fv-text-muted);
    }

    .login-hint p { margin: 0; }

    .login-hint code {
      background: var(--fv-surface-hover);
      padding: 1px 4px;
      border-radius: 3px;
      font-size: 11px;
    }

    /* Completion summary */
    .completion-summary {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px;
      background: var(--fv-status-success-bg);
      border-radius: 8px;
      margin-top: 16px;
    }

    .completion-summary.cancelled { background: var(--fv-surface-hover); }
    .completion-summary.failed { background: var(--fv-status-error-bg); }

    .completion-icon {
      font-size: 24px;
    }

    .completion-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
      font-size: 14px;
      color: var(--fv-text);
    }

    .completion-text span {
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    /* Error */
    .error-message {
      padding: 10px 12px;
      background: var(--fv-status-error-bg);
      border-radius: 4px;
      margin-top: 12px;
      font-size: 13px;
      color: var(--fv-status-error-fg);
    }

    /* Dialog helpers */
    .dialog-comments {
      margin-top: 12px;
      padding: 8px 12px;
      background: var(--fv-surface-hover);
      border-radius: 4px;
      font-size: 13px;
    }

    .dialog-comments p {
      margin: 4px 0 0 0;
      color: var(--fv-text);
    }

    .dialog-textarea {
      width: 100%;
      margin-top: 8px;
    }

    .dialog-field {
      margin-top: 12px;
    }

    .dialog-field label {
      display: block;
      margin-bottom: 4px;
      font-size: 13px;
      font-weight: 500;
    }

    .dialog-field input {
      width: 100%;
    }

    .required {
      color: var(--fv-status-error-fg);
    }

    /* Add/Remove step */
    .remove-step-btn {
      margin-left: auto;
      min-width: 24px;
      padding: 0 4px;
      font-size: 12px;
      color: var(--fv-text-muted);
      opacity: 0;
      transition: opacity 0.15s ease;
    }

    .step-header:hover .remove-step-btn {
      opacity: 1;
    }

    .remove-step-btn:hover {
      color: var(--fv-status-error-fg);
    }

    .toggle-row {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
    }

    .toggle {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      cursor: pointer;
    }

    .remove-warning {
      margin-top: 8px;
      font-size: 13px;
      color: var(--fv-status-error-fg);
      font-weight: 500;
    }
  `],
  imports: [CommonModule, FormsModule, ButtonsModule, DialogModule, InputsModule]
})
export class ChangeWorkflowComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  /** The change ID whose workflow to display */
  @Input() changeId!: string;

  /** Optional: pass a known workflowInstanceId to skip the entity lookup */
  @Input() workflowInstanceId?: string;

  /** Current user's LoginId — required for claim/approve/reject/delegate/skip actions */
  @Input() currentLoginId?: string;

  /** Emitted when a workflow step action completes */
  @Output() stepCompleted = new EventEmitter<ChangeWorkflowStepEvent>();

  /** Emitted when the entire workflow completes (all steps done) */
  @Output() workflowCompleted = new EventEmitter<WorkflowInstance>();

  // Expose enum to template
  WorkflowState = WorkflowState;

  workflow?: WorkflowInstance;
  progress?: WorkflowProgress;
  isLoading = false;
  isProcessing = false;
  errorMessage = '';

  // Inline comments for current step
  comments = '';

  // Confirm dialog state
  showConfirmDialog = false;
  showDelegateDialogVisible = false;
  pendingAction?: 'approve' | 'reject' | 'skip';
  pendingStep?: WorkflowStep;
  rejectReason = '';
  skipReason = '';
  delegateTo = '';
  delegateReason = '';

  // Add/Remove step dialog state
  showAddStepDialog = false;
  showRemoveStepDialog = false;
  removeStepTarget?: WorkflowStep;
  newStep: AddWorkflowStepRequest = this.getEmptyNewStep();

  ngOnInit(): void {
    this.loadWorkflow();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['changeId'] && !changes['changeId'].firstChange) ||
      (changes['workflowInstanceId'] && !changes['workflowInstanceId'].firstChange)
    ) {
      this.loadWorkflow();
    }
  }

  async loadWorkflow(): Promise<void> {
    if (!this.changeId && !this.workflowInstanceId) return;

    this.isLoading = true;
    this.errorMessage = '';
    try {
      let instanceId = this.workflowInstanceId;

      // Resolve instance ID from entity if not provided
      if (!instanceId && this.changeId) {
        const instance = await this.api.getWorkflowForEntity('Change', this.changeId).toPromise();
        instanceId = instance?.id;
      }

      if (!instanceId) {
        this.workflow = undefined;
        this.progress = undefined;
        return;
      }

      // Load full instance and progress in parallel
      const [instance, prog] = await Promise.all([
        this.api.getWorkflow(instanceId, true).toPromise(),
        this.api.getWorkflowProgress(instanceId).toPromise()
      ]);

      this.workflow = instance;
      this.progress = prog;
    } catch (error) {
      console.error('Failed to load workflow', error);
      this.workflow = undefined;
      this.progress = undefined;
    } finally {
      this.isLoading = false;
    }
  }

  /** Public method for consumers to trigger a refresh */
  reload(): void {
    this.loadWorkflow();
  }

  // ========== Step logic ==========

  isCurrentStep(step: WorkflowStep): boolean {
    if (!this.workflow || this.workflow.state !== WorkflowState.InProgress) return false;
    return step.stepIndex === this.workflow.currentStepIndex &&
           (step.state === WorkflowStepState.Pending || step.state === WorkflowStepState.InProgress);
  }

  canAct(step: WorkflowStep): boolean {
    return this.isCurrentStep(step) && !!this.currentLoginId;
  }

  isAssignedToCurrentUser(step: WorkflowStep): boolean {
    if (!this.currentLoginId || !step.assignedTo) return false;
    return step.assignedTo.toLowerCase() === this.currentLoginId.toLowerCase();
  }

  canApprove(step: WorkflowStep): boolean {
    if (!step.instance?.template) {
      // No template info available, default to allowing approve
      return true;
    }
    return true; // The API will enforce allowed decisions
  }

  canReject(step: WorkflowStep): boolean {
    return true; // The API will enforce allowed decisions
  }

  isStepOverdue(step: WorkflowStep): boolean {
    if (!step.dueDate) return false;
    return new Date(step.dueDate) < new Date() &&
           (step.state === WorkflowStepState.Pending || step.state === WorkflowStepState.InProgress);
  }

  // ========== Actions ==========

  async doClaim(step: WorkflowStep): Promise<void> {
    if (!this.currentLoginId) return;

    this.isProcessing = true;
    this.errorMessage = '';
    try {
      const result = await this.api.claimStep(step.id, {}, this.currentLoginId).toPromise();
      if (result?.success) {
        this.stepCompleted.emit({
          action: 'claimed',
          step,
          result,
          workflowComplete: false,
          workflowState: result.workflowState
        });
        await this.loadWorkflow();
      } else {
        this.errorMessage = result?.errorMessage || 'Failed to claim step.';
      }
    } catch (error: any) {
      this.errorMessage = error?.error?.message || error?.message || 'Failed to claim step.';
    } finally {
      this.isProcessing = false;
    }
  }

  openConfirmDialog(action: 'approve' | 'reject' | 'skip', step: WorkflowStep): void {
    this.pendingAction = action;
    this.pendingStep = step;
    this.rejectReason = '';
    this.skipReason = '';
    this.showConfirmDialog = true;
  }

  openDelegateDialog(step: WorkflowStep): void {
    this.pendingStep = step;
    this.delegateTo = '';
    this.delegateReason = '';
    this.showDelegateDialogVisible = true;
  }

  closeDialogs(): void {
    this.showConfirmDialog = false;
    this.showDelegateDialogVisible = false;
    this.showAddStepDialog = false;
    this.showRemoveStepDialog = false;
    this.pendingAction = undefined;
    this.pendingStep = undefined;
    this.removeStepTarget = undefined;
  }

  // ========== Add / Remove Steps ==========

  /** Whether the user can add/remove steps (workflow must be in-progress and user must be identified) */
  get canModifySteps(): boolean {
    return !!this.workflow &&
           this.workflow.state === WorkflowState.InProgress &&
           !!this.currentLoginId;
  }

  /** A pending step can be removed if it's not the current step */
  canRemoveStep(step: WorkflowStep): boolean {
    if (!this.canModifySteps) return false;
    return step.state === WorkflowStepState.Pending &&
           step.stepIndex !== this.workflow!.currentStepIndex;
  }

  private getEmptyNewStep(): AddWorkflowStepRequest {
    return {
      stepName: '',
      description: undefined,
      assignedToRole: undefined,
      assignedToUserName: undefined,
      dueDays: undefined,
      canSkip: false,
      requiresComments: false
    };
  }

  openAddStepDialog(): void {
    this.newStep = this.getEmptyNewStep();
    this.showAddStepDialog = true;
  }

  openRemoveStepDialog(step: WorkflowStep): void {
    this.removeStepTarget = step;
    this.showRemoveStepDialog = true;
  }

  async doAddStep(): Promise<void> {
    if (!this.workflow || !this.newStep.stepName?.trim()) return;

    this.isProcessing = true;
    this.errorMessage = '';
    try {
      const result = await this.api.addWorkflowStep(this.workflow.id, {
        ...this.newStep,
        stepName: this.newStep.stepName.trim(),
        dueDays: this.newStep.dueDays ? Number(this.newStep.dueDays) : undefined
      }).toPromise();

      if (result?.success) {
        this.closeDialogs();
        await this.loadWorkflow();
      } else {
        this.errorMessage = result?.errorMessage || 'Failed to add step.';
      }
    } catch (error: any) {
      this.errorMessage = error?.error?.message || error?.message || 'Failed to add step.';
    } finally {
      this.isProcessing = false;
    }
  }

  async doRemoveStep(): Promise<void> {
    if (!this.workflow || !this.removeStepTarget) return;

    this.isProcessing = true;
    this.errorMessage = '';
    try {
      const result = await this.api.removeWorkflowStep(
        this.workflow.id,
        this.removeStepTarget.id
      ).toPromise();

      if (result?.success) {
        this.closeDialogs();
        await this.loadWorkflow();
      } else {
        this.errorMessage = result?.errorMessage || 'Failed to remove step.';
      }
    } catch (error: any) {
      this.errorMessage = error?.error?.message || error?.message || 'Failed to remove step.';
    } finally {
      this.isProcessing = false;
    }
  }

  async doApprove(): Promise<void> {
    if (!this.pendingStep) return;
    await this.executeStepAction('approved', async () => {
      return this.api.approveStep(this.pendingStep!.id, {
        comments: this.comments?.trim() || undefined
      }).toPromise();
    });
  }

  async doReject(): Promise<void> {
    if (!this.pendingStep || !this.rejectReason?.trim()) return;
    await this.executeStepAction('rejected', async () => {
      return this.api.rejectStep(this.pendingStep!.id, {
        reason: this.rejectReason.trim()
      }).toPromise();
    });
  }

  async doSkip(): Promise<void> {
    if (!this.pendingStep) return;
    await this.executeStepAction('skipped', async () => {
      return this.api.skipStep(this.pendingStep!.id, {
        reason: this.skipReason?.trim() || undefined
      }).toPromise();
    });
  }

  async doDelegate(): Promise<void> {
    if (!this.pendingStep || !this.delegateTo?.trim()) return;
    await this.executeStepAction('delegated', async () => {
      return this.api.delegateStep(this.pendingStep!.id, {
        delegateTo: this.delegateTo.trim(),
        reason: this.delegateReason?.trim() || undefined
      }).toPromise();
    });
  }

  private async executeStepAction(
    action: ChangeWorkflowStepEvent['action'],
    apiFn: () => Promise<WorkflowStepResult | undefined>
  ): Promise<void> {
    this.isProcessing = true;
    this.errorMessage = '';
    try {
      const result = await apiFn();
      if (result?.success) {
        const workflowComplete = result.workflowState === WorkflowState.Completed;

        this.stepCompleted.emit({
          action,
          step: this.pendingStep!,
          result,
          workflowComplete,
          workflowState: result.workflowState
        });

        this.closeDialogs();
        this.comments = '';
        await this.loadWorkflow();

        if (workflowComplete && this.workflow) {
          this.workflowCompleted.emit(this.workflow);
        }
      } else {
        this.errorMessage = result?.errorMessage || 'Action failed. Please try again.';
      }
    } catch (error: any) {
      this.errorMessage = error?.error?.message || error?.message || 'An unexpected error occurred.';
    } finally {
      this.isProcessing = false;
    }
  }

  // ========== Display helpers ==========

  getWorkflowStateClass(state: WorkflowState): string {
    switch (state) {
      case WorkflowState.Pending: return 'pending';
      case WorkflowState.InProgress: return 'in-progress';
      case WorkflowState.Completed: return 'completed';
      case WorkflowState.Cancelled: return 'cancelled';
      case WorkflowState.Failed: return 'failed';
      default: return '';
    }
  }

  getWorkflowStateLabel(state: WorkflowState): string {
    switch (state) {
      case WorkflowState.Pending: return 'Pending';
      case WorkflowState.InProgress: return 'In Progress';
      case WorkflowState.Completed: return 'Completed';
      case WorkflowState.Cancelled: return 'Cancelled';
      case WorkflowState.Failed: return 'Failed';
      default: return 'Unknown';
    }
  }

  getStepRowClass(step: WorkflowStep): string {
    return this.isCurrentStep(step) ? 'current' : '';
  }

  getStepDotClass(step: WorkflowStep): string {
    switch (step.state) {
      case WorkflowStepState.Approved: return 'approved';
      case WorkflowStepState.Rejected: return 'rejected';
      case WorkflowStepState.Skipped: return 'skipped';
      case WorkflowStepState.Delegated: return 'delegated';
      case WorkflowStepState.InProgress: return 'in-progress';
      case WorkflowStepState.Pending: return 'pending';
      default: return '';
    }
  }

  getStepIcon(step: WorkflowStep): string {
    switch (step.state) {
      case WorkflowStepState.Approved: return '✓';
      case WorkflowStepState.Rejected: return '✗';
      case WorkflowStepState.Skipped: return '↷';
      case WorkflowStepState.Delegated: return '⇄';
      case WorkflowStepState.InProgress: return '●';
      case WorkflowStepState.Pending: return (step.stepIndex + 1).toString();
      default: return (step.stepIndex + 1).toString();
    }
  }

  getStepStateBadgeClass(state: WorkflowStepState): string {
    switch (state) {
      case WorkflowStepState.Approved: return 'approved';
      case WorkflowStepState.Rejected: return 'rejected';
      case WorkflowStepState.Skipped: return 'skipped';
      case WorkflowStepState.Delegated: return 'delegated';
      case WorkflowStepState.InProgress: return 'in-progress';
      case WorkflowStepState.Pending: return 'pending';
      default: return '';
    }
  }

  getStepStateLabel(state: WorkflowStepState): string {
    switch (state) {
      case WorkflowStepState.Pending: return 'Pending';
      case WorkflowStepState.InProgress: return 'In Progress';
      case WorkflowStepState.Approved: return 'Approved';
      case WorkflowStepState.Rejected: return 'Rejected';
      case WorkflowStepState.Skipped: return 'Skipped';
      case WorkflowStepState.Delegated: return 'Delegated';
      default: return 'Unknown';
    }
  }

  getDecisionLabel(decision: WorkflowDecision): string {
    switch (decision) {
      case WorkflowDecision.Approve: return 'Approved';
      case WorkflowDecision.Reject: return 'Rejected';
      case WorkflowDecision.RequestChanges: return 'Requested Changes';
      case WorkflowDecision.Delegate: return 'Delegated';
      default: return 'Unknown';
    }
  }

  getDecisionClass(decision: WorkflowDecision): string {
    switch (decision) {
      case WorkflowDecision.Approve: return 'approve';
      case WorkflowDecision.Reject: return 'reject';
      case WorkflowDecision.Delegate: return 'delegate';
      default: return '';
    }
  }
}
